import { NgIf, NgIfContext } from '@angular/common';
import { computed, Directive, effect, inject, input, TemplateRef } from '@angular/core';
import { AuthStore } from './auth.store';

/**
 * V4 replacement for {@link HasRoleDirective}. Renders the host element only when the user has
 * one of the required permissions (PLATFORM_ADMIN bypasses; PLATFORM_SUPPORT bypasses on
 * `*:read`). Use this for new code; the role directive remains for the migration window.
 *
 * Usage: `<button *libHasPermission="'gateway:write'">Configure</button>`
 *        `<button *libHasPermission="['site:configure-files', 'site-template:admin']">…</button>`
 */
@Directive({
  selector: '[libHasPermission]',
  standalone: true,
  hostDirectives: [NgIf],
})
export class HasPermissionDirective {
  libHasPermission = input.required<string | string[]>();
  libHasPermissionElse = input<TemplateRef<NgIfContext<unknown>>>();

  #store = inject(AuthStore);
  #ngIf = inject(NgIf, { host: true });

  private readonly showTemplate = computed(() => {
    if (!this.#store.isLoggedIn()) {
      return false;
    }
    return this.#store.hasPermission(this.libHasPermission());
  });

  constructor() {
    effect(() => {
      this.#ngIf.ngIf = this.showTemplate();
      const elseRef = this.libHasPermissionElse();
      if (elseRef) {
        this.#ngIf.ngIfElse = elseRef;
      }
    });
  }
}
